/**
 * 이어 하기 — 진행 중인 판 번호와 이름을 아직 안 남긴 첫 발견을 이 브라우저에 기억한다.
 *
 * 새로고침하거나 폰에서 앱을 오가며 탭이 다시 불려도 같은 판에 이어 붙는다(Windows 방울이 prod 지적 2026-11-04).
 * 판 자체는 서버 캐시에 60분 남는다 — 여기는 번호만 둔다. 쓸 수 없는 브라우저면 조용히 아무것도 안 한다.
 */
import type { Item } from './api';

const GAME_KEY = 'pop.alchemist.game.v1';
const CLAIM_KEY = 'pop.alchemist.claim.v1';
const SETTLE_KEY = 'pop.alchemist.settle.v1';
const CLAIM_WINDOW_MS = 60 * 60 * 1000; // 서버의 이름 남기기 시간과 같다

/** 앞 턴 결과를 보관함에 넣을지 버릴지 — 다음 턴 요청에 실려 가므로, 그 전에 새로고침해도 잃지 않게 기억한다 */
export interface SavedSettle {
    game: string;
    turn: number;
    action: 'keep' | 'discard';
    replace: number | null;
}

export interface SavedClaim {
    token: string;
    item: Item;
    legend: boolean;
    first: boolean;
    at: number;
}

function read<T>(key: string): T | null {
    try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
    } catch {
        return null;
    }
}

function write(key: string, value: unknown): void {
    try {
        if (value === null) localStorage.removeItem(key);
        else localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // 사생활 모드 등 — 이어 하기만 안 될 뿐 게임은 돈다
    }
}

export const resume = {
    gameId: (): string | null => {
        const id = read<string>(GAME_KEY);
        return typeof id === 'string' && /^[A-Za-z0-9]{32}$/.test(id) ? id : null;
    },
    saveGame: (id: string | null) => write(GAME_KEY, id),

    claim: (): SavedClaim | null => {
        const saved = read<SavedClaim>(CLAIM_KEY);
        if (!saved || typeof saved.token !== 'string' || Date.now() - Number(saved.at) > CLAIM_WINDOW_MS) {
            write(CLAIM_KEY, null);
            return null;
        }
        return saved;
    },
    saveClaim: (claim: SavedClaim | null) => write(CLAIM_KEY, claim),

    settle: (): SavedSettle | null => read<SavedSettle>(SETTLE_KEY),
    saveSettle: (settle: SavedSettle | null) => write(SETTLE_KEY, settle),
};
